import { isObject, isString } from 'lodash'

import { logger } from '~/debug/logger'
import routeDirectory from '~/navigation/routes'

import parseBillingDraft from './parseBillingDraft'
import type { BillingComposerEntity, BillingDraft } from './types'

/**
 * A draft produced on a page without a form (detail view, billing hub) is stashed in sessionStorage and
 * applied by the wizard when it mounts, or when a matching stash arrives while that wizard is already
 * open (`router.push` to the current URL does not remount). Session scope keeps it out of other tabs
 * and drops it when the browser closes. Keys include organization and user so a switch of either
 * cannot consume another identity's stash.
 */

const KEY_PREFIX = 'billingComposer.pendingDraft.'
const SESSION_ID_PREFIX = 'billingComposer.conversation.'

type PendingDraftStashedListener = (key: string) => void
const pendingDraftStashedListeners = new Set<PendingDraftStashedListener>()

/** Same-tab notice that sessionStorage gained a pending draft. The `storage` event does not fire here. */
export function subscribePendingDraftStashed(listener: PendingDraftStashedListener): () => void {
  pendingDraftStashedListeners.add(listener)
  return () => {
    pendingDraftStashedListeners.delete(listener)
  }
}

/** Tenant + user identity that every sessionStorage key for this feature must include. */
export type BillingComposerSessionScope = {
  organizationId: string
  userId: string
}

export type PendingDraft = {
  draft: BillingDraft
  /** Change paths the user ticked before navigating; undefined applies the whole draft. */
  selectedPaths?: string[]
  storedAt: number
}

/** Storage key for a wizard: the entity id when editing, or the `new-<entity>` create page. */
export function pendingDraftKey(
  entity: BillingComposerEntity,
  entityId: string | undefined,
  scope: BillingComposerSessionScope
): string {
  return `${KEY_PREFIX}${scope.organizationId}.${scope.userId}.${entityId ?? `new-${entity}`}`
}

export function stashPendingDraft(key: string, pending: Omit<PendingDraft, 'storedAt'>): boolean {
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ ...pending, storedAt: Date.now() }))
    pendingDraftStashedListeners.forEach((listener) => listener(key))
    return true
  } catch (error) {
    logger.warn('Billing composer: could not stash pending draft', error)
    return false
  }
}

/** Reads and removes the stashed draft so it is applied at most once. */
export function takePendingDraft(key: string): PendingDraft | null {
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return null
    window.sessionStorage.removeItem(key)
    const parsed: unknown = JSON.parse(raw)
    if (!isObject(parsed)) return null
    const value = parsed as Record<string, unknown>
    const draft = parseBillingDraft(value.draft, 'pending-draft')
    if (!draft) return null
    return {
      draft,
      selectedPaths: Array.isArray(value.selectedPaths) ? value.selectedPaths.filter(isString) : undefined,
      storedAt: typeof value.storedAt === 'number' ? value.storedAt : Date.now()
    }
  } catch (error) {
    logger.warn('Billing composer: could not read pending draft', error)
    return null
  }
}

/** The wizard that applies a stashed draft: the edit page of its target, or the create page. */
export function draftWizardRoute(entity: BillingComposerEntity, targetId: string | undefined): string {
  if (entity === 'product') {
    return targetId ? routeDirectory.settings.products.edit(targetId) : routeDirectory.settings.products.new
  }
  return targetId ? routeDirectory.settings.agreements.edit(targetId) : routeDirectory.settings.agreements.new
}

/**
 * Stashes a draft for the wizard that will apply it and returns that wizard's route, or null when the
 * stash failed (including when identity scope is missing). Update drafts go to the edit page of
 * `draft.targetId` (or `fallbackTargetId`, the entity of the page the draft was made on); create
 * drafts go to the create page.
 */
export function stashDraftForWizard(
  draft: BillingDraft,
  selectedPaths: string[] | undefined,
  scope: BillingComposerSessionScope | null | undefined,
  fallbackTargetId?: string
): string | null {
  if (!scope) return null
  const targetId = draft.mode === 'update' ? (draft.targetId ?? fallbackTargetId) : undefined
  const stashed = stashPendingDraft(pendingDraftKey(draft.entity, targetId, scope), { draft, selectedPaths })
  return stashed ? draftWizardRoute(draft.entity, targetId) : null
}

/** Per-page conversation id so closing and reopening the panel (or a reload) resumes the same thread. */
export function conversationSessionKey(pathname: string, scope: BillingComposerSessionScope): string {
  return `${SESSION_ID_PREFIX}${scope.organizationId}.${scope.userId}.${pathname}`
}

export function readSessionConversationId(
  pathname: string,
  scope: BillingComposerSessionScope | null | undefined
): string | null {
  if (!scope) return null
  try {
    return window.sessionStorage.getItem(conversationSessionKey(pathname, scope))
  } catch {
    return null
  }
}

export function writeSessionConversationId(
  pathname: string,
  conversationId: string | null,
  scope: BillingComposerSessionScope | null | undefined
): void {
  if (!scope) return
  try {
    if (conversationId) {
      window.sessionStorage.setItem(conversationSessionKey(pathname, scope), conversationId)
    } else {
      window.sessionStorage.removeItem(conversationSessionKey(pathname, scope))
    }
  } catch {
    // Session storage may be unavailable (private mode quotas); the in-memory state still works.
  }
}
